import _ from 'lodash';
import React from 'react';

import {
  errorUtil,
  fields,
  formUtil,
  validationUtil
} from 'iron-forge-react-modules';
import {
  FormModal,
  LabelledText,
  stepUtil,
  TransferList,
  unitConversionUtil
} from 'modules';
import {
  Controller,
  useForm
} from 'react-hook-form';

import {
  Box,
  Button,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import Big from 'big.js';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const StyledImage = styled('img')({});



export interface IProps {
  returnAuthorizationGroupId: string;

  onClose: () => void;
  onSubmit: () => void;
}

export const FC: React.FC<IProps> = props => {
  const {
    step,
    previousButtonText,
    submitButtonText,
    goToNext,
    goToPrev
  } = stepUtil.useSteps({
    steps: [
      'selectReturnAuthorizations',
      'cartonSpecifications',
      'shippingAddress',
      'confirm',
      'printLabel'
    ]
  });
  const queryTuple = dataService.useQuery(props.returnAuthorizationGroupId);
  const [
    generateShippingLabel,
    generateShippingLabelTuple
  ] = dataService.useGenerateShippingLabelForTier1ReturnAuthorizations();
  const [
    submittedFormStateForConfirm,
    setSubmittedFormStateForConfirm
  ] = React.useState<types.IFormState>(queryTuple.data.formState);
  const form = useForm<types.IFormState>({
    defaultValues: submittedFormStateForConfirm
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['returnAuthorizations']);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      if (step === 'confirm') {
        await errorUtil.errorWrap(async () => {
          if (window.confirm("Are you sure you want to build this carton? This will generate an EasyPost shipping label and create a NetSuite print request")) {
            await generateShippingLabel(formState);
            goToNext();
          }
        }, {
          NO_RETURNS_SELECTED: "Select at least one return to generate a shipping label",
          CANNOT_MAKE_LABEL_FOR_MULTIPLE_RETURNS: "Cannot generate a shipping label for more than 1 return group.",
          RETURN_HAS_SHIPMENT: "A selected return authorization already has a shipment.",
          INVALID_WEIGHT_UNIT_FOUND: "A weight unit was found for an item which does not correspond to a known value.",
          ERROR_CREATING_CARTON: "There was an error creating the carton",
          ERROR_CREATING_SHIPMENT: "There was an error creating the shipment",
          ERROR_CREATING_LABEL: "There was an error creating the label with EasyPost"
        });

      } else {
        if (step === 'selectReturnAuthorizations') {
          const totalWeightOunces = formState.returnAuthorizations
            .filter(rma => rma.isSelected)
            .reduce((sum, line) => {
              const rma = queryTuple.data.id2Return[line.returnAuthorizationId];
              return unitConversionUtil.convertWeight(rma.weightValue, rma.weightUnit, 'ounces').plus(sum);
            }, new Big(0));

          const num = totalWeightOunces.toFixed(2, 3);
          form.setValue('weightOunces', num);
        }

        if (step === 'cartonSpecifications') {
          const dims = [
            formState.widthInches,
            formState.lengthInches,
            formState.heightInches,
            formState.weightOunces
          ];

          if (_.some(dims, dim => new Big(dim).lte(0))) {
            window.alert("Valid dimensions and weight must be greater than 0");
            return;
          }
        }

        setSubmittedFormStateForConfirm({
          ...formState,
          returnAuthorizations: [
            ...formState.returnAuthorizations
          ]
        });

        goToNext();
      }
    },
    [
      form,
      queryTuple.data.id2Return,
      step,
      generateShippingLabel,
      goToNext
    ]
  );

  const items = React.useMemo(
    () => queryTuple.data.formState.returnAuthorizations.map((item): TransferList.IItem => ({
      id: item.returnAuthorizationId,
      primaryText: queryTuple.data.id2Return[item.returnAuthorizationId].serialNumber,
      secondaryText: queryTuple.data.id2Return[item.returnAuthorizationId].sku
    })),
    [
      queryTuple.data.formState.returnAuthorizations,
      queryTuple.data.id2Return
    ]
  );

  const loading = queryTuple.loading || generateShippingLabelTuple.loading;

  const defaultProps: Omit<fields.TextField.ITextFieldControllerProps<types.IFormState>, 'fieldKey'> = {
    isRequired: true,
    isDisabled: loading,
    disableDefaultMargins: true,
    formControl: form.control
  };

  return (
    <FormModal.FC
      isDoubleWide={step === 'selectReturnAuthorizations' || step === 'confirm'}
      isLoading={loading}
      title="Print Label"
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}
      fnGenElCloseButton={args => step !== 'printLabel' ? (
        <Button
          {...args}
          disabled={loading}
          onClick={() => {
            goToPrev();
            if (step === 'selectReturnAuthorizations') {
              props.onClose();
            }
          }}>
          {previousButtonText}
        </Button>
      ) : <Button />}
      fnGenElAcceptButton={args => step !== 'printLabel' ? (
        <Button
          {...args}
          variant="contained"
          type="submit"
          disabled={loading}>
          {submitButtonText}
        </Button>
      ) : (
        <Button onClick={() => props.onSubmit()}>
          Complete
        </Button>
      )}>
      {/* Select return authorizations for carton */}
      {step === 'selectReturnAuthorizations' && (
        <Controller
          name="returnAuthorizations"
          control={form.control}
          render={({ field }) => (
            <TransferList.FC
              allItems={items}
              loading={queryTuple.loading}
              id2IsSelected={_.mapValues(_.keyBy(field.value, v => v.returnAuthorizationId), v => v.isSelected)}
              onChange={val => {
                field.onChange(_.values(_.mapValues(val, (isSelected, returnAuthorizationId) => ({
                  isSelected,
                  returnAuthorizationId
                }))));
              }} />
          )} />
      )}

      {/* The form step for carton specifications (dimensions, weight) */}
      {step === 'cartonSpecifications' && (
        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={4}>
            <ControlledTextField
              {...defaultProps}
              label="Length (in)"
              fieldKey="lengthInches"
              extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
          </Grid>

          <Grid
            item
            xs={4}>
            <ControlledTextField
              {...defaultProps}
              label="Width (in)"
              fieldKey="widthInches"
              extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
          </Grid>

          <Grid
            item
            xs={4}>
            <ControlledTextField
              {...defaultProps}
              label="Height (in)"
              fieldKey="heightInches"
              extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
          </Grid>

          <Grid
            item
            xs={9}>
            <ControlledTextField
              {...defaultProps}
              label="Weight (oz)"
              fieldKey="weightOunces"
              extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
          </Grid>
        </Grid>
      )}

      {step === 'shippingAddress' && (
        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={6}>
            <ControlledTextField
              {...defaultProps}
              label="Company"
              fieldKey="shippingCompany" />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              {...defaultProps}
              label="Phone"
              fieldKey="shippingPhone" />
          </Grid>

          <Grid item xs={9}>
            <ControlledTextField
              {...defaultProps}
              label="Street Address"
              fieldKey="shippingStreet1" />
          </Grid>

          <Grid item xs={3}>
            <ControlledTextField
              {...defaultProps}
              isRequired={false}
              label="Unit"
              fieldKey="shippingStreet2" />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              {...defaultProps}
              label="City"
              fieldKey="shippingCity" />
          </Grid>

          <Grid item xs={2}>
            <ControlledTextField
              {...defaultProps}
              label="State"
              fieldKey="shippingUsStateAbbrev" />
          </Grid>

          <Grid item xs={4}>
            <ControlledTextField
              {...defaultProps}
              label="Postal code"
              fieldKey="shippingPostalCode" />
          </Grid>
        </Grid>
      )}

      {step === 'confirm' && (
        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={12}>
            <Typography>
              Confirm the details of the carton to create
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      SKU
                    </TableCell>

                    <TableCell>
                      Model
                    </TableCell>

                    <TableCell>
                      IMEI
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {submittedFormStateForConfirm.returnAuthorizations.filter(rma => rma.isSelected).map(rma => (
                    <TableRow key={rma.returnAuthorizationId}>
                      <TableCell>
                        {queryTuple.data.id2Return[rma.returnAuthorizationId].sku}
                      </TableCell>

                      <TableCell>
                        {queryTuple.data.id2Return[rma.returnAuthorizationId].itemName}
                      </TableCell>

                      <TableCell>
                        {queryTuple.data.id2Return[rma.returnAuthorizationId].serialNumber}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid
            item
            xs={6}>
            <Typography
              variant="h2"
              sx={{ mb: 1 }}>
              Carton Specifications
            </Typography>

            <LabelledText.FC
              direction="horizontal"
              title="Length"
              text={submittedFormStateForConfirm.lengthInches + '"'} />

            <LabelledText.FC
              direction="horizontal"
              title="Width"
              text={submittedFormStateForConfirm.widthInches + '"'} />

            <LabelledText.FC
              direction="horizontal"
              title="Height"
              text={submittedFormStateForConfirm.heightInches + '"'} />

            <LabelledText.FC
              direction="horizontal"
              title="Weight"
              text={submittedFormStateForConfirm.weightOunces + 'oz'} />
          </Grid>

          <Grid
            item
            xs={6}>
            <Typography
              variant="h2"
              sx={{ mb: 1 }}>
              Ship To
            </Typography>

            {submittedFormStateForConfirm.shippingCompany && (
              <LabelledText.FC
                direction="horizontal"
                title="Company"
                text={submittedFormStateForConfirm.shippingCompany || 'Not provided'} />
            )}

            {submittedFormStateForConfirm.shippingPhone && (
              <LabelledText.FC
                direction="horizontal"
                title="Phone"
                text={submittedFormStateForConfirm.shippingPhone || 'Not provided'} />
            )}

            <LabelledText.FC
              direction="horizontal"
              title="Street 1"
              text={submittedFormStateForConfirm.shippingStreet1} />

            {submittedFormStateForConfirm.shippingStreet2 && (
              <LabelledText.FC
                direction="horizontal"
                title="Unit"
                text={submittedFormStateForConfirm.shippingStreet2} />
            )}

            <LabelledText.FC
              direction="horizontal"
              title="City"
              text={submittedFormStateForConfirm.shippingCity} />

            <LabelledText.FC
              direction="horizontal"
              title="State"
              text={submittedFormStateForConfirm.shippingUsStateAbbrev} />

            <LabelledText.FC
              direction="horizontal"
              title="Postal Code"
              text={submittedFormStateForConfirm.shippingPostalCode} />
          </Grid>
        </Grid>
      )}

      {step === 'printLabel' && (
        <Grid
          item
          xs={12}>
          {generateShippingLabelTuple.data?.labelUrl ? (
            <a
              href={generateShippingLabelTuple.data?.labelUrl ?? ''}
              rel="noreferrer"
              target="_blank">
              <StyledImage
                src={generateShippingLabelTuple.data?.labelUrl}
                sx={{
                  width: '100%'
                }} />
            </a>
          ) : (
            <Box>
              <Typography>
                Unable to generate label
              </Typography>
            </Box>
          )}
        </Grid>
      )}
    </FormModal.FC>
  );
};
